import { useEffect, useMemo, useCallback } from 'react';
import {
  useHealthAndSafetyState,
  useHealthAndSafetyDispatch,
  ACTION_TYPE,
} from '../contexts/healthAndSafety.context';
import {
  useHealthIncidentListHook,
  useCreateHealthIncidentHook,
  useUpdateHealthIncidentHook,
  useDeleteHealthIncidentHook,
} from './useHealthIncidentHooks';
import {
  useSafetyProtocolListHook,
  useCreateSafetyProtocolHook,
  useUpdateSafetyProtocolHook,
  useDeleteSafetyProtocolHook,
} from './useSafetyProtocolHooks';
import {
  useEmergencyContactListHook,
  useCreateEmergencyContactHook,
  useUpdateEmergencyContactHook,
  useDeleteEmergencyContactHook,
} from './useEmergencyContactHooks';
import {
  useHealthCheckupListHook,
  useCreateHealthCheckupHook,
  useUpdateHealthCheckupHook,
  useDeleteHealthCheckupHook,
} from './useHealthCheckupHooks';
import {
  THealthIncident,
  TSafetyProtocol,
  TEmergencyContact,
  THealthCheckup,
} from '../../../../apis/administration/administration.interfaces';
import {
  THealthIncidentParams,
  TSafetyProtocolParams,
  TEmergencyContactParams,
  THealthCheckupParams,
} from '../../../../apis/healthAndSafety/healthAndSafety.api.interfaces';
import { TDocument } from '../../../../apis/documents/documents.interfaces';
import ERROR_MESSAGE from '../../../../utils/ErrorMessages';

export const useHealthAndSafety = () => {
  const state = useHealthAndSafetyState();
  const dispatch = useHealthAndSafetyDispatch();

  // Default params for initial fetch
  const incidentParams: THealthIncidentParams = useMemo(
    () => ({
      page: 1,
      limit: 20,
    }),
    []
  );

  const protocolParams: TSafetyProtocolParams = useMemo(
    () => ({
      page: 1,
      limit: 20,
      isActive: true,
    }),
    []
  );

  const contactParams: TEmergencyContactParams = useMemo(
    () => ({
      page: 1,
      limit: 50, // Get all emergency contacts
      isActive: true,
    }),
    []
  );

  const checkupParams: THealthCheckupParams = useMemo(
    () => ({
      page: 1,
      limit: 20,
    }),
    []
  );

  // ===================== QUERIES =====================
  const {
    data: incidentsResponse,
    isLoading: isLoadingIncidents,
    error: incidentsError,
    refetch: refetchIncidents,
  } = useHealthIncidentListHook(incidentParams);

  const {
    data: protocolsResponse,
    isLoading: isLoadingProtocols,
    error: protocolsError,
    refetch: refetchProtocols,
  } = useSafetyProtocolListHook(protocolParams);

  const {
    data: contactsResponse,
    isLoading: isLoadingContacts,
    error: contactsError,
    refetch: refetchContacts,
  } = useEmergencyContactListHook(contactParams);

  const {
    data: checkupsResponse,
    isLoading: isLoadingCheckups,
    error: checkupsError,
    refetch: refetchCheckups,
  } = useHealthCheckupListHook(checkupParams);

  // ===================== MUTATIONS =====================

  // Health Incidents
  const createIncidentMutation = useCreateHealthIncidentHook({
    onError: (error: any) => {
      dispatch({
        type: ACTION_TYPE.SET_ERROR,
        payload: error?.message || ERROR_MESSAGE.GENERIC_ERROR,
      });
    },
  });

  const updateIncidentMutation = useUpdateHealthIncidentHook({
    onSuccess: (data) => {
      dispatch({ type: ACTION_TYPE.UPDATE_INCIDENT, payload: data });
    },
    onError: (error: any) => {
      dispatch({
        type: ACTION_TYPE.SET_ERROR,
        payload: error?.message || ERROR_MESSAGE.GENERIC_ERROR,
      });
    },
  });

  const deleteIncidentMutation = useDeleteHealthIncidentHook({
    onSuccess: (_, id) => {
      dispatch({ type: ACTION_TYPE.DELETE_INCIDENT, payload: id });
    },
    onError: (error: any) => {
      dispatch({
        type: ACTION_TYPE.SET_ERROR,
        payload: error?.message || ERROR_MESSAGE.GENERIC_ERROR,
      });
    },
  });

  // Safety Protocols
  const createProtocolMutation = useCreateSafetyProtocolHook({
    onError: (error: any) => {
      dispatch({
        type: ACTION_TYPE.SET_ERROR,
        payload: error?.message || ERROR_MESSAGE.GENERIC_ERROR,
      });
    },
  });

  const updateProtocolMutation = useUpdateSafetyProtocolHook({
    onSuccess: (data) => {
      dispatch({ type: ACTION_TYPE.UPDATE_PROTOCOL, payload: data });
    },
    onError: (error: any) => {
      dispatch({
        type: ACTION_TYPE.SET_ERROR,
        payload: error?.message || ERROR_MESSAGE.GENERIC_ERROR,
      });
    },
  });

  const deleteProtocolMutation = useDeleteSafetyProtocolHook({
    onSuccess: (_, id) => {
      dispatch({ type: ACTION_TYPE.DELETE_PROTOCOL, payload: id });
    },
    onError: (error: any) => {
      dispatch({
        type: ACTION_TYPE.SET_ERROR,
        payload: error?.message || ERROR_MESSAGE.GENERIC_ERROR,
      });
    },
  });

  // Emergency Contacts
  const createContactMutation = useCreateEmergencyContactHook({
    onError: (error: any) => {
      dispatch({
        type: ACTION_TYPE.SET_ERROR,
        payload: error?.message || ERROR_MESSAGE.GENERIC_ERROR,
      });
    },
  });

  const updateContactMutation = useUpdateEmergencyContactHook({
    onSuccess: (data) => {
      dispatch({ type: ACTION_TYPE.UPDATE_CONTACT, payload: data });
    },
    onError: (error: any) => {
      dispatch({
        type: ACTION_TYPE.SET_ERROR,
        payload: error?.message || ERROR_MESSAGE.GENERIC_ERROR,
      });
    },
  });

  const deleteContactMutation = useDeleteEmergencyContactHook({
    onSuccess: (_, id) => {
      dispatch({ type: ACTION_TYPE.DELETE_CONTACT, payload: id });
    },
    onError: (error: any) => {
      dispatch({
        type: ACTION_TYPE.SET_ERROR,
        payload: error?.message || ERROR_MESSAGE.GENERIC_ERROR,
      });
    },
  });

  // Health Checkups
  const createCheckupMutation = useCreateHealthCheckupHook({
    onError: (error: any) => {
      dispatch({
        type: ACTION_TYPE.SET_ERROR,
        payload: error?.message || ERROR_MESSAGE.GENERIC_ERROR,
      });
    },
  });

  const updateCheckupMutation = useUpdateHealthCheckupHook({
    onSuccess: (data) => {
      // Transform API response (string[] attachments) to administration interface (TDocument[] attachments)
      const transformedCheckup: THealthCheckup = {
        ...data,
        attachments: data.attachments
          ? (data.attachments as unknown as string[]).map(
              (url: string): TDocument => ({
                documentId: url,
                userId: '',
                documentType: 'other' as any,
                documentName: url.split('/').pop() || 'Document',
                submissionDate: new Date().toISOString(),
                status: 'pending' as any,
                uploadedAt: {
                  userId: '',
                  timestamp: Date.now(),
                },
                fileUrl: url,
              })
            )
          : undefined,
      };
      dispatch({
        type: ACTION_TYPE.UPDATE_CHECKUP,
        payload: transformedCheckup,
      });
    },
    onError: (error: any) => {
      dispatch({
        type: ACTION_TYPE.SET_ERROR,
        payload: error?.message || ERROR_MESSAGE.GENERIC_ERROR,
      });
    },
  });

  const deleteCheckupMutation = useDeleteHealthCheckupHook({
    onSuccess: (_, id) => {
      dispatch({ type: ACTION_TYPE.DELETE_CHECKUP, payload: id });
    },
    onError: (error: any) => {
      dispatch({
        type: ACTION_TYPE.SET_ERROR,
        payload: error?.message || ERROR_MESSAGE.GENERIC_ERROR,
      });
    },
  });

  // ===================== EFFECTS =====================
  // Update context when data changes
  useEffect(() => {
    if (incidentsResponse?.data) {
      dispatch({
        type: ACTION_TYPE.SET_INCIDENTS,
        payload: incidentsResponse.data,
      });
    }
  }, [incidentsResponse, dispatch]);

  useEffect(() => {
    if (protocolsResponse?.data) {
      dispatch({
        type: ACTION_TYPE.SET_PROTOCOLS,
        payload: protocolsResponse.data,
      });
    }
  }, [protocolsResponse, dispatch]);

  useEffect(() => {
    if (contactsResponse?.data) {
      dispatch({
        type: ACTION_TYPE.SET_EMERGENCY_CONTACTS,
        payload: contactsResponse.data,
      });
    }
  }, [contactsResponse, dispatch]);

  useEffect(() => {
    if (checkupsResponse?.data) {
      // Transform API response (string[] attachments) to administration interface (TDocument[] attachments)
      const transformedCheckups: THealthCheckup[] = checkupsResponse.data.map(
        (checkup) => ({
          ...checkup,
          attachments: checkup.attachments
            ? (checkup.attachments as unknown as string[]).map(
                (url: string): TDocument => ({
                  documentId: url,
                  userId: '',
                  documentType: 'other' as any,
                  documentName: url.split('/').pop() || 'Document',
                  submissionDate: new Date().toISOString(),
                  status: 'pending' as any,
                  uploadedAt: {
                    userId: '',
                    timestamp: Date.now(),
                  },
                  fileUrl: url,
                })
              )
            : undefined,
        })
      );
      dispatch({
        type: ACTION_TYPE.SET_HEALTH_CHECKUPS,
        payload: transformedCheckups,
      });
    }
  }, [checkupsResponse, dispatch]);

  // Handle loading state
  useEffect(() => {
    const isLoading =
      isLoadingIncidents ||
      isLoadingProtocols ||
      isLoadingContacts ||
      isLoadingCheckups;
    dispatch({ type: ACTION_TYPE.SET_LOADING, payload: isLoading });
  }, [
    isLoadingIncidents,
    isLoadingProtocols,
    isLoadingContacts,
    isLoadingCheckups,
    dispatch,
  ]);

  // Handle errors
  useEffect(() => {
    const firstError =
      incidentsError || protocolsError || contactsError || checkupsError;
    if (firstError) {
      dispatch({
        type: ACTION_TYPE.SET_ERROR,
        payload: (firstError as any)?.message || ERROR_MESSAGE.GENERIC_ERROR,
      });
    }
  }, [incidentsError, protocolsError, contactsError, checkupsError, dispatch]);

  // ===================== CALLBACK FUNCTIONS =====================

  // Health Incidents
  const createIncident = useCallback(
    async (
      incident: Omit<THealthIncident, 'id' | 'createdAt' | 'updatedAt'>
    ) => {
      try {
        const newIncident = await createIncidentMutation.mutateAsync(incident);
        dispatch({ type: ACTION_TYPE.ADD_INCIDENT, payload: newIncident });
        return newIncident;
      } catch (error) {
        throw error;
      }
    },
    [createIncidentMutation, dispatch]
  );

  const updateIncident = useCallback(
    async (id: string, data: Partial<THealthIncident>) => {
      try {
        return await updateIncidentMutation.mutateAsync({ id, data });
      } catch (error) {
        throw error;
      }
    },
    [updateIncidentMutation]
  );

  const deleteIncident = useCallback(
    async (id: string) => {
      try {
        await deleteIncidentMutation.mutateAsync(id);
      } catch (error) {
        throw error;
      }
    },
    [deleteIncidentMutation]
  );

  // Safety Protocols
  const createProtocol = useCallback(
    async (
      protocol: Omit<TSafetyProtocol, 'id' | 'createdAt' | 'updatedAt'>
    ) => {
      try {
        const newProtocol = await createProtocolMutation.mutateAsync(protocol);
        dispatch({ type: ACTION_TYPE.ADD_PROTOCOL, payload: newProtocol });
        return newProtocol;
      } catch (error) {
        throw error;
      }
    },
    [createProtocolMutation, dispatch]
  );

  const updateProtocol = useCallback(
    async (id: string, data: Partial<TSafetyProtocol>) => {
      try {
        return await updateProtocolMutation.mutateAsync({ id, data });
      } catch (error) {
        throw error;
      }
    },
    [updateProtocolMutation]
  );

  const deleteProtocol = useCallback(
    async (id: string) => {
      try {
        await deleteProtocolMutation.mutateAsync(id);
      } catch (error) {
        throw error;
      }
    },
    [deleteProtocolMutation]
  );

  // Emergency Contacts
  const createContact = useCallback(
    async (
      contact: Omit<TEmergencyContact, 'id' | 'createdAt' | 'updatedAt'>
    ) => {
      try {
        const newContact = await createContactMutation.mutateAsync(contact);
        dispatch({ type: ACTION_TYPE.ADD_CONTACT, payload: newContact });
        return newContact;
      } catch (error) {
        throw error;
      }
    },
    [createContactMutation, dispatch]
  );

  const updateContact = useCallback(
    async (id: string, data: Partial<TEmergencyContact>) => {
      try {
        return await updateContactMutation.mutateAsync({ id, data });
      } catch (error) {
        throw error;
      }
    },
    [updateContactMutation]
  );

  const deleteContact = useCallback(
    async (id: string) => {
      try {
        await deleteContactMutation.mutateAsync(id);
      } catch (error) {
        throw error;
      }
    },
    [deleteContactMutation]
  );

  // Health Checkups
  const createCheckup = useCallback(
    async (checkup: Omit<THealthCheckup, 'id' | 'createdAt' | 'updatedAt'>) => {
      try {
        // Transform input: convert TDocument[] attachments to string[] for API
        const apiCheckupInput = {
          ...checkup,
          attachments: checkup.attachments
            ? checkup.attachments.map((doc) => doc.fileUrl || doc.documentId)
            : undefined,
        } as Omit<
          import('../../../../apis/healthAndSafety/healthAndSafety.api.interfaces').THealthCheckup,
          'id' | 'createdAt' | 'updatedAt'
        >;

        const apiCheckup =
          await createCheckupMutation.mutateAsync(apiCheckupInput);

        // Transform output: convert string[] attachments to TDocument[] for administration interface
        const transformedCheckup: THealthCheckup = {
          ...apiCheckup,
          attachments: apiCheckup.attachments
            ? (apiCheckup.attachments as unknown as string[]).map(
                (url: string): TDocument => ({
                  documentId: url,
                  userId: '',
                  documentType: 'other' as any,
                  documentName: url.split('/').pop() || 'Document',
                  submissionDate: new Date().toISOString(),
                  status: 'pending' as any,
                  uploadedAt: {
                    userId: '',
                    timestamp: Date.now(),
                  },
                  fileUrl: url,
                })
              )
            : undefined,
        };
        dispatch({
          type: ACTION_TYPE.ADD_CHECKUP,
          payload: transformedCheckup,
        });
        return transformedCheckup;
      } catch (error) {
        throw error;
      }
    },
    [createCheckupMutation, dispatch]
  );

  const updateCheckup = useCallback(
    async (id: string, data: Partial<THealthCheckup>) => {
      try {
        // Transform input: convert TDocument[] attachments to string[] for API
        const apiData = {
          ...data,
          attachments: data.attachments
            ? data.attachments.map((doc) => doc.fileUrl || doc.documentId)
            : undefined,
        } as Partial<
          import('../../../../apis/healthAndSafety/healthAndSafety.api.interfaces').THealthCheckup
        >;

        const apiCheckup = await updateCheckupMutation.mutateAsync({
          id,
          data: apiData,
        });

        // Transform output: convert string[] attachments to TDocument[] for administration interface
        const transformedCheckup: THealthCheckup = {
          ...apiCheckup,
          attachments: apiCheckup.attachments
            ? (apiCheckup.attachments as unknown as string[]).map(
                (url: string): TDocument => ({
                  documentId: url,
                  userId: '',
                  documentType: 'other' as any,
                  documentName: url.split('/').pop() || 'Document',
                  submissionDate: new Date().toISOString(),
                  status: 'pending' as any,
                  uploadedAt: {
                    userId: '',
                    timestamp: Date.now(),
                  },
                  fileUrl: url,
                })
              )
            : undefined,
        };
        return transformedCheckup;
      } catch (error) {
        throw error;
      }
    },
    [updateCheckupMutation]
  );

  const deleteCheckup = useCallback(
    async (id: string) => {
      try {
        await deleteCheckupMutation.mutateAsync(id);
      } catch (error) {
        throw error;
      }
    },
    [deleteCheckupMutation]
  );

  const selectIncident = useCallback(
    (incident: THealthIncident | undefined) => {
      dispatch({ type: ACTION_TYPE.SELECT_INCIDENT, payload: incident });
    },
    [dispatch]
  );

  const selectProtocol = useCallback(
    (protocol: TSafetyProtocol | undefined) => {
      dispatch({ type: ACTION_TYPE.SELECT_PROTOCOL, payload: protocol });
    },
    [dispatch]
  );

  const selectContact = useCallback(
    (contact: TEmergencyContact | undefined) => {
      dispatch({ type: ACTION_TYPE.SELECT_CONTACT, payload: contact });
    },
    [dispatch]
  );

  const selectCheckup = useCallback(
    (checkup: THealthCheckup | undefined) => {
      dispatch({ type: ACTION_TYPE.SELECT_CHECKUP, payload: checkup });
    },
    [dispatch]
  );

  const setSearchTerm = useCallback(
    (term: string) => {
      dispatch({ type: ACTION_TYPE.SET_SEARCH_TERM, payload: term });
    },
    [dispatch]
  );

  const setCategoryFilter = useCallback(
    (category: string) => {
      dispatch({ type: ACTION_TYPE.SET_CATEGORY_FILTER, payload: category });
    },
    [dispatch]
  );

  const clearError = useCallback(() => {
    dispatch({ type: ACTION_TYPE.SET_ERROR, payload: undefined });
  }, [dispatch]);

  return {
    // State
    incidents: state.incidents,
    protocols: state.protocols,
    emergencyContacts: state.emergencyContacts,
    healthCheckups: state.healthCheckups,
    selectedIncident: state.selectedIncident,
    selectedProtocol: state.selectedProtocol,
    selectedContact: state.selectedContact,
    selectedCheckup: state.selectedCheckup,
    filteredIncidents: state.filteredIncidents,
    filteredProtocols: state.filteredProtocols,
    searchTerm: state.searchTerm,
    selectedCategoryFilter: state.selectedCategoryFilter,
    isLoading: state.isLoading,
    errorMessage: state.errorMessage,

    // Actions - Health Incidents
    createIncident,
    updateIncident,
    deleteIncident,
    selectIncident,

    // Actions - Safety Protocols
    createProtocol,
    updateProtocol,
    deleteProtocol,
    selectProtocol,

    // Actions - Emergency Contacts
    createContact,
    updateContact,
    deleteContact,
    selectContact,

    // Actions - Health Checkups
    createCheckup,
    updateCheckup,
    deleteCheckup,
    selectCheckup,

    // Actions - General
    setSearchTerm,
    setCategoryFilter,
    clearError,

    // Refetch functions
    refetchIncidents,
    refetchProtocols,
    refetchContacts,
    refetchCheckups,
  };
};
